/**
 * `seen`, posted on a presented frame by `model/seenMark`'s rule, for both players. The marks
 * belong to the mounted player: a new opening starts with none, so it tries again.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useRef } from 'react';

import { attentionQueueQueryKey } from '~/entities/client';
import { clipTimelineQueryKey, type ClipEntry } from '~/entities/exercise-clip';
import { postClipsClipIdSeen } from '~/shared/api/generated/endpoints/clip/clip';

import { seenFailed, seenGaveUp, seenIsDue, type SeenMark } from '../model/seenMark';

interface SeenVariables {
  readonly clipId: number;
  readonly exerciseId: number;
}

/**
 * What a player's writes tell the rest of the app: the attention queue reads what the trainer saw
 * and answered, and the host refreshes what it lists from it.
 */
export function useClipActivity(onActivity: () => void): () => void {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: attentionQueueQueryKey });
    onActivity();
  };
}

/**
 * @param onSeen a mark landed; the caller refreshes what reads it.
 * @param onGaveUp the mark was refused on every attempt; called once per mount.
 * @returns what a presented frame of a clip calls.
 */
export function useClipSeen(
  clientId: number,
  onSeen: () => void,
  onGaveUp?: () => void,
): (clipId: number, exerciseId: number) => void {
  const queryClient = useQueryClient();
  const marks = useRef(new Map<number, SeenMark>());
  const saidGaveUp = useRef(false);
  const seen = useMutation({
    mutationFn: ({ clipId }: SeenVariables) => postClipsClipIdSeen(clipId),
    // On the mutation rather than on `mutate`, so the answer is handled even after the player has
    // closed or a second clip's mark has replaced this one on the observer.
    onSuccess: (_, { clipId, exerciseId }) => {
      marks.current.set(clipId, { kind: 'done' });
      queryClient.setQueryData<readonly ClipEntry[]>(
        clipTimelineQueryKey(clientId, exerciseId),
        (entries) => entries?.map((entry) => (entry.id === clipId ? { ...entry, unread: false } : entry)),
      );
      onSeen();
    },
    onError: (_, { clipId }) => {
      const next = seenFailed(marks.current.get(clipId), Date.now());
      marks.current.set(clipId, next);
      if (seenGaveUp(next) && !saidGaveUp.current) {
        saidGaveUp.current = true;
        onGaveUp?.();
      }
    },
  });

  return (clipId, exerciseId) => {
    if (!seenIsDue(marks.current.get(clipId), Date.now())) return;
    marks.current.set(clipId, { kind: 'sending' });
    seen.mutate({ clipId, exerciseId });
  };
}
